import React, {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	useImperativeHandle,
} from "react";

import {
	gsap,
} from "gsap";

import {
	Expo,
} from "gsap/all";

import {
	useParams,
} from "react-router";

import {
	useLocation,
	useNavigate,
} from "react-router-dom";

import caseData from "../data/cases/cases.json";

import {
	isMobileOrTablet,
	isTablet,
} from "../hooks/isMobile";

import {
	sendGACustomEvent,
} from "../hooks/googleAnalytics";

import useLocalStorage from "../hooks/useLocalStorage";

import useFilterFromLocation from "../hooks/useFilterFromLocation";

import {
	caseMatchesFilter,
	getAvailableCaseFilters,
	getFilterLabel,
	getFilterOverviewPath,
	normalizeCaseFilter,
} from "../data/cases/caseFilterHelpers";

import SolutionsSlider from "./components/SolutionsSlider";
import SolutionsFullscreenText from "./components/SolutionsFullscreenText";
import SolutionsControls from "./components/SolutionsControls";
import SolutionsUtilityLayer from "./components/SolutionsUtilityLayer";

import styles from "./Solutions.module.css";

const clamp = (
	num,
	min,
	max,
) =>
	Math.min(
		Math.max(
			num,
			min,
		),
		max,
	);

const lerp = (
	f0,
	f1,
	t,
) => {
	return (
		(1 - t) *
			f0 +
		t *
			f1
	);
};

/*
 * ============================================================
 * KNOWN CASE FILTERS
 * ============================================================
 *
 * Used only to distinguish:
 *
 * /cases/fortnite
 *
 * from a legacy:
 *
 * /cases/project-name
 */
const availableCaseFilterSet =
	new Set([
		"all",
		...getAvailableCaseFilters(
			caseData,
		),
	]);

const isKnownCaseFilter = (
	filter,
) => {
	return availableCaseFilterSet.has(
		normalizeCaseFilter(
			filter,
		),
	);
};

const getWindowSize =
	() => {
		const vw =
			Math.max(
				document
					.documentElement
					.clientWidth ||
					0,

				window.innerWidth ||
					0,
			);

		const vh =
			Math.max(
				document
					.documentElement
					.clientHeight ||
					0,

				window.innerHeight ||
					0,
			);

		return {
			vw,
			vh,
		};
	};

const Solutions =
	React.forwardRef(
		(
			props,
			ref,
		) => {
			const location =
				useLocation();

			const navigate =
				useNavigate();

			const wildcardPath =
				useParams()["*"];

			const isOfflineMode =
				process.env
					.REACT_APP_OFFLINE ===
				"offline_mode";

			const [
				fullAccess,
			] = useLocalStorage(
				"full-access",
				true,
			);

			const [
				isEnabled,
				setIsEnabled,
			] = useState(
				false,
			);

			const caseFilter =
				normalizeCaseFilter(
					useFilterFromLocation(),
				);

			const caseFilterRef =
				useRef(
					caseFilter,
				);

			const casesRef =
				useRef(null);

			const initialCaseFilterTriggered =
				useRef(false);

			/*
			 * ====================================================
			 * CASE FILTERING
			 * ====================================================
			 */

			const getFilteredCases =
				(filter) => {
					const normalizedFilter =
						normalizeCaseFilter(
							filter,
						);

					const isFullAccess =
						fullAccess ||
						location.pathname.includes(
							"/showcase/",
						);

					const caseDataPrefilter =
						!isFullAccess
							? caseData.filter(
									(
										caseItem,
									) =>
										caseItem[
											"full-access"
										] !==
										"full-access",
							  )
							: caseData.filter(
									(
										caseItem,
									) =>
										caseItem[
											"full-access"
										] !==
										"none-full-access",
							  );

					if (
						normalizedFilter ===
						"all"
					) {
						return caseDataPrefilter.slice(
							1,
						);
					}

					const filteredCases =
						caseDataPrefilter.filter(
							(
								caseItem,
							) =>
								caseMatchesFilter(
									caseItem,
									normalizedFilter,
								),
						);

					if (
						normalizedFilter ===
						"work"
					) {
						return filteredCases.slice(
							1,
						);
					}

					return filteredCases;
				};

			const [
				cases,
				setCases,
			] = useState([
				getFilteredCases(
					caseFilter,
				),

				Date.now(),
			]);

			casesRef.current =
				cases[0];

			const refreshCases =
				() => {
					const filteredCases =
						getFilteredCases(
							caseFilter,
						);

					casesRef.current =
						filteredCases;

					setCases([
						filteredCases,
						Date.now(),
					]);
				};

			/*
			 * ====================================================
			 * ACTIVE SLIDE STATE
			 * ====================================================
			 */

			const [
				preloaded,
				setPreloaded,
			] = useState(
				false,
			);

			const [
				activeSlide,
				setActiveSlide,
			] = useState(
				-1,
			);

			const activeSlideRef =
				useRef(-1);

			const [
				nextLiveSlide,
				setNextLiveSlide,
			] = useState(
				-1,
			);

			const nextLiveSlideRef =
				useRef(-1);

			const activeCase =
				useRef(null);

			const [
				activeVideo,
				setActiveVideo,
			] = useState(
				-1,
			);

			const handleSetActiveCase =
				(
					curActiveCase,
				) => {
					activeCase.current =
						curActiveCase;
				};

			/*
			 * ====================================================
			 * VIEW / SLIDER REFS
			 * ====================================================
			 */

			const initedSlider =
				useRef(false);

			const [
				isPortraitMode,
				setIsPortraitMode,
			] = useState(
				false,
			);

			const isPortraitModeRef =
				useRef(null);

			const slideWidthPercentage =
				useRef(
					0.3654843,
				);

			const slidesPerPage =
				useRef(
					1 /
						slideWidthPercentage.current,
				);

			const slideWidth =
				useRef(-1);

			const wrapWidth =
				useRef(-1);

			const sliderRef =
				useRef(null);

			const sliderWrapperRef =
				useRef(null);

			const blackOverlayRef =
				useRef(null);

			const blackOverlayGradientRef =
				useRef(null);

			const blackOverlayGradientTopRef =
				useRef(null);

			const progressBar =
				useRef(0);

			const bulletProgressBar =
				useRef(null);

			const slides =
				useRef([
					[],
					[],
				]);

			/*
			 * ====================================================
			 * BUTTON / FULLSCREEN REFS
			 * ====================================================
			 */

			const btnParallaxSliderRef =
				useRef(null);

			const btnParallaxWatchRef =
				useRef(null);

			const btnTimelineCntRef =
				useRef(false);

			const fullscreenText_v2 =
				useRef(null);

			const fullscreenSolutionRef =
				useRef(null);

			const videoModalRef =
				useRef(null);

			/*
			 * Legacy hidden title refs.
			 */
			const titleContainer =
				useRef(0);

			const centerTitleIndex =
				useRef(null);

			const centerTitleName =
				useRef(null);

			/*
			 * ====================================================
			 * SLIDER MOVEMENT
			 * ====================================================
			 */

			const progress =
				useRef(0);

			const xProgress =
				useRef(0);

			const playrate =
				useRef(0);

			const requestRef =
				useRef();

			const raf =
				useRef(null);

			const rafCallback =
				useRef(null);

			const handleProgress =
				(
					_caller,
					newProgress,
				) => {
					progress.current =
						newProgress;
				};

			/*
			 * ====================================================
			 * POINTER / GESTURE STATE
			 * ====================================================
			 */

			const dragging =
				useRef(false);

			const onscroll =
				useRef(false);

			const startY =
				useRef(0);

			const startProgress =
				useRef(-1);

			const startDrag =
				useRef({
					x: 0,
					y: 0,
				});

			const dragDirection =
				useRef(-1);

			const lockScroll =
				useRef(false);

			const preventHorizontalSwipe =
				useRef(false);

			const wasDrag =
				useRef(false);

			const wasSearchClose =
				useRef(false);

			const wasTextVisible =
				useRef(false);

			const isLongPress =
				useRef(false);

			const longPressTimeout =
				useRef(null);

			const wheelTimeout =
				useRef(null);

			const lastEventTouchStart =
				useRef(null);

			const lastEventTouchStartTime =
				useRef(0);

			/*
			 * ====================================================
			 * SCROLL / OVERLAY STATE
			 * ====================================================
			 */

			const scrollDeltaYTarget =
				useRef(0);

			const scrollDeltaYCurrent =
				useRef(0);

			const scrollDirection =
				useRef(0);

			const prevScroll =
				useRef(0);

			const overflowScrolling =
				useRef(
					typeof document.body
						.style[
						"-webkit-overflow-scrolling"
					] !==
						"undefined",
				);

			const [
				fsTextScrollEnabled,
				setFsTextScrollEnabled,
			] = useState(
				false,
			);

			const fsTextScrollEnabledRef =
				useRef(false);

			const currentOpacityBlackOverlay =
				useRef(-1);

			const overlayInTransRef =
				useRef(false);

			const [
				overlayInTrans,
				setOverlayInTrans,
			] = useState(
				false,
			);

			const overlayCoolDown =
				useRef(1000);

			const overlayTransitionTimer =
				useRef(null);

			const fsTxtEnabledTimer =
				useRef(null);

			const scrollForMoreTl =
				useRef(null);

			const textVisibility =
				useRef(false);

			/*
			 * ====================================================
			 * HOVER / AUTOPLAY
			 * ====================================================
			 */

			const isCurrentlyHoveredRef =
				useRef(false);

			const [
				isCurrentlyHovered,
				setIsCurrentlyHovered,
			] = useState(
				false,
			);

			const hoverTimeout =
				useRef(null);

			const [
				autoPlay,
				setAutoplay,
			] = useState(
				null,
			);

			/*
			 * ====================================================
			 * MOBILE
			 * ====================================================
			 */

			const mobileSwipeDecisionPercentage =
				useRef(0);

			const mobileSwipeDecisionOverlay =
				useRef(null);

			const windowSize =
				useRef(
					getWindowSize(),
				);

			const initialViewVertical =
				useRef(
					windowSize.current
						.vw <
						windowSize.current
							.vh,
				);

			const [
				curViewModeIsVertical,
				setCurViewModeIsVertical,
			] = useState(
				windowSize.current
					.vh <
					windowSize.current
						.vw,
			);

			const [
				curScale,
				setCurScale,
			] = useState(
				0,
			);

			/*
			 * ====================================================
			 * VIDEO MODAL
			 * ====================================================
			 */

			const videoModalOpenRef =
				useRef(false);

			const videoModalAutoCloseRef =
				useRef(false);

			/*
			 * ====================================================
			 * ROUTING
			 * ====================================================
			 */

			const getCaseFromWildcardPath =
				() => {
					const wildCardSplit =
						wildcardPath
							?.split(
								"/",
							)
							.filter(
								(
									str,
								) =>
									str !==
									"",
							) ??
						[];

					const isCasesRoute =
						location.pathname ===
							"/cases" ||
						location.pathname.startsWith(
							"/cases/",
						);

					if (
						isCasesRoute &&
						wildCardSplit.length ===
							1 &&
						isKnownCaseFilter(
							wildCardSplit[
								0
							],
						)
					) {
						return -1;
					}

					const caseFiltered =
						wildCardSplit.length >
						0
							? wildCardSplit.length >
							  1
								? wildCardSplit[
										1
								  ]
								: wildCardSplit[
										0
								  ]
							: "";

					let slideIndex =
						casesRef.current?.findIndex(
							(x) =>
								x.shorthand ===
								caseFiltered,
						);

					if (
						slideIndex ===
						-1
					) {
						slideIndex =
							casesRef.current?.findIndex(
								(x) =>
									x.shorthand ===
									location.pathname
										.replaceAll(
											"/showcase/",
											"",
										)
										.replaceAll(
											"/",
											"",
										),
							);
					}

					return slideIndex;
				};

			const handleSetActiveSlide =
				(
					_dispatcher,
					next,
				) => {
					if (
						activeSlideRef.current ===
						next
					) {
						return;
					}

					activeSlideRef.current =
						next;

					setActiveSlide(
						next,
					);

					setNextLiveSlide(
						next,
					);

					if (
						next ===
						-1
					) {
						navigate(
							getFilterOverviewPath(
								caseFilterRef.current,
							) +
								(
									location.search ??
									""
								),
							{
								replace:
									false,
							},
						);

						return;
					}

					if (
						location.pathname ===
						`/${
							casesRef
								.current?.[
								next
							]
								?.shorthand
						}`
					) {
						return;
					}

					navigate(
						`/cases/${
							caseFilterRef.current !==
							"all"
								? caseFilterRef.current +
								  "/"
								: ""
						}${
							casesRef
								.current?.[
								next
							]
								?.shorthand
						}` +
							(
								location.search ??
								""
							),
						{
							replace:
								false,
						},
					);
				};

			const setNewFilter =
				(newFilter) => {
					const normalizedNewFilter =
						normalizeCaseFilter(
							newFilter,
						);

					caseFilterRef.current =
						normalizedNewFilter;

					handleSlideExit();

					navigate(
						getFilterOverviewPath(
							normalizedNewFilter,
						) +
							(
								location.search ??
								""
							),
						{
							replace:
								false,
						},
					);
				};

			useImperativeHandle(
				ref,
				() => {
					return {
						setFilter(
							newFilter,
						) {
							setNewFilter(
								newFilter,
							);
						},
					};
				},
				[],
			);

			/*
			 * ====================================================
			 * BASIC STATE HELPERS
			 * ====================================================
			 */

			const handleOverlayInTransChange =
				(inTrans) => {
					overlayInTransRef.current =
						inTrans;

					setOverlayInTrans(
						inTrans,
					);
				};

			const handleFsTextScrollEnabledChange =
				(
					nextEnabled,
				) => {
					fsTextScrollEnabledRef.current =
						nextEnabled;

					setFsTextScrollEnabled(
						nextEnabled,
					);
				};

			const handleSetAutoplay =
				(
					bool,
					_caller = "",
				) => {
					if (
						bool !==
						autoPlay
					) {
						setAutoplay(
							bool,
						);
					}
				};

			const resetActiveVideo =
				() => {
					if (
						!isPortraitModeRef.current
					) {
						setActiveVideo(
							-1,
						);
					} else {
						setActiveVideo(
							2,
						);
					}
				};

			const getCurrentCase =
				() => {
					return (
						casesRef
							.current?.[
							activeSlideRef.current ??
								-1
						] ??
						null
					);
				};

			/*
			 * ====================================================
			 * VIDEO MODAL
			 * ====================================================
			 */

			const handleVideoCloseRequest =
				() => {
					if (
						!videoModalOpenRef.current
					) {
						return;
					}

					const autoClose =
						videoModalAutoCloseRef.current;

					handleVideoModalOpen(
						false,
					);

					if (
						!isPortraitModeRef.current &&
						autoClose
					) {
						handleSlideExit();
					}
				};

			const handleVideoChromeVisibilityChange =
				(
					visible,
					instant = false,
				) => {
					if (
						!videoModalOpenRef.current
					) {
						return;
					}

					props.changeHeaderState(
						"custom_back_visibility",
						{
							visible,
							instant,
						},
					);
				};

			const handleVideoModalOpen =
				(
					bool,
					autoClose = false,
				) => {
					if (
						!videoModalRef.current
							?.open
					) {
						return;
					}

					if (bool) {
						videoModalOpenRef.current =
							true;

						videoModalAutoCloseRef.current =
							autoClose;

						props.changeHeaderState(
							"custom_back_visibility",
							{
								visible:
									false,

								instant:
									true,
							},
						);

						handleChangeHeaderState(
							"handleVideoModalOpen",
							"player",
							autoClose,
						);

						videoModalRef.current.open(
							true,
							handleVideoCloseRequest,
						);

						requestAnimationFrame(
							() => {
								gsap.set(
									sliderRef.current,
									{
										autoAlpha:
											0,
									},
								);
							},
						);

						handleFsTextScrollEnabledChange(
							false,
						);

						sendGACustomEvent(
							props.globalContext,
							"fs",
							activeCase
								.current
								?.shorthand,
						);

						return;
					}

					videoModalOpenRef.current =
						false;

					videoModalAutoCloseRef.current =
						false;

					videoModalRef.current.open(
						false,
					);

					sendGACustomEvent(
						props.globalContext,
						"fe",
						activeCase
							.current
							?.shorthand,
					);

					requestAnimationFrame(
						() => {
							gsap.set(
								sliderRef.current,
								{
									autoAlpha:
										1,
								},
							);
						},
					);

					handleFsTextScrollEnabledChange(
						true,
					);

					handleChangeHeaderState(
						"handleVideoModalOpen",
						"active_slide",
					);
				};

			/*
			 * ====================================================
			 * SLIDER MOVEMENT
			 * ====================================================
			 */

			const setSlidePercentage =
				(mobile) => {
					if (mobile) {
						slideWidthPercentage.current =
							1;
					} else {
						slideWidthPercentage.current =
							0.3654843;
					}

					slidesPerPage.current =
						1 /
						slideWidthPercentage.current;
				};

			const animate =
				() => {
					if (
						raf.current
					) {
						raf.current();
					}

					if (
						rafCallback.current
					) {
						rafCallback.current();

						rafCallback.current =
							null;
					}

					requestRef.current =
						requestAnimationFrame(
							animate,
						);
				};

			const move = (
				clampOutput = true,
				instant = false,
				callback = () => {},
			) => {
				if (
					clampOutput
				) {
					handleProgress(
						"move clamped",

						clamp(
							progress.current,
							0,

							wrapWidth.current -
								(
									casesRef
										.current
										?.length <
									slidesPerPage.current
										? casesRef
												.current
												?.length
										: slidesPerPage.current
								) *
									slideWidth.current,
						),
					);
				}

				if (
					instant
				) {
					xProgress.current =
						progress.current;

					if (
						sliderRef.current
					) {
						sliderRef.current.style.transform =
							`translate3d(${-xProgress.current}px,0,0)`;
					}
				}

				callback?.();
			};

			const handleMoveToSlide =
				(
					index,
					instant = false,
					callback = () => {},
				) => {
					const centerOffset =
						(
							slideWidth.current *
							slidesPerPage.current
						) /
							2 -
						slideWidth.current /
							2;

					handleProgress(
						"handleMoveToSlide",

						slideWidth.current *
							index -
							centerOffset,
					);

					move(
						true,
						instant,
						callback,
					);
				};

			const getNextLiveSlide =
				(
					title = false,
				) => {
					const curProgress =
						title
							? progress.current +
							  slideWidth.current /
									2
							: progress.current;

					return clamp(
						parseInt(
							curProgress /
								slideWidth.current,
						),

						0,

						casesRef.current
							?.length -
							1,
					);
				};

			/*
			 * ====================================================
			 * LONG PRESS / WHEEL
			 * ====================================================
			 */

			const clearLongPressTimeout =
				() => {
					if (
						longPressTimeout.current
					) {
						clearTimeout(
							longPressTimeout.current,
						);
					}
				};

			const setLongPressTimeout =
				() => {
					clearLongPressTimeout();

					longPressTimeout.current =
						setTimeout(
							() => {
								isLongPress.current =
									true;
							},

							1000,
						);
				};

			const clearWheelTimeout =
				() => {
					if (
						wheelTimeout.current
					) {
						clearTimeout(
							wheelTimeout.current,
						);
					}
				};

			const setWheelTimeout =
				() => {
					clearWheelTimeout();

					wheelTimeout.current =
						setTimeout(
							() => {
								onscroll.current =
									false;
							},

							300,
						);
				};

			/*
			 * ====================================================
			 * SCROLL / BLACK OVERLAY
			 * ====================================================
			 */

			const isTextScrolledUp =
				() => {
					const curScrollTop =
						fullscreenText_v2
							.current
							?.scrollTop ||
						0;

					scrollDirection.current =
						curScrollTop -
						prevScroll.current;

					prevScroll.current =
						curScrollTop;

					return (
						curScrollTop >
						(
							scrollDirection.current >
								-200 &&
							curScrollTop >
								30
								? 10
								: 10
						)
					);
				};

			const handleTxtVisibility =
				(bool) => {
					if (
						textVisibility.current ===
						bool
					) {
						return;
					}

					textVisibility.current =
						bool;

					fullscreenSolutionRef
						.current
						?.textVisibility?.(
							bool,
						);

					resetActiveVideo();
				};

			const blackZeroValue =
				0;

			const refreshBlackOverlay =
				(
					caller,
					forcedValue = -1,
					hoverOverwrite = false,
				) => {
					if (
						(
							isCurrentlyHoveredRef.current &&
							!hoverOverwrite
						) ||
						caller ===
							"setOverlayTransitionTimer"
					) {
						return;
					}

					let desiredOpacityBlack =
						-1;

					let desiredScrollForMore =
						-1;

					if (
						forcedValue ===
						-1
					) {
						if (
							isTextScrolledUp()
						) {
							desiredOpacityBlack =
								1;

							desiredScrollForMore =
								0;

							handleChangeHeaderState(
								"isTextScrolledUp",
								"active_title",
							);

							handleTxtVisibility(
								true,
							);
						} else {
							desiredOpacityBlack =
								blackZeroValue;

							desiredScrollForMore =
								1;

							handleChangeHeaderState(
								"isTextScrolledUp",
								"active_slide",
							);

							handleTxtVisibility(
								false,
							);
						}
					} else if (
						forcedValue ===
							1 ||
						forcedValue ===
							2
					) {
						desiredOpacityBlack =
							1;
					} else if (
						forcedValue ===
							0 ||
						forcedValue ===
							3
					) {
						desiredOpacityBlack =
							0;
					}

					if (
						activeSlideRef.current ===
						-1
					) {
						// Nothing active.
					} else if (
						desiredScrollForMore ===
						1
					) {
						scrollForMoreTl.current?.restart?.(
							true,
						);

						handleSetAutoplay(
							true,
							"refreshBlackOverlay",
						);
					} else if (
						desiredScrollForMore ===
						0
					) {
						handleSetAutoplay(
							false,
							"refreshBlackOverlay",
						);
					}

					if (
						currentOpacityBlackOverlay.current ===
						desiredOpacityBlack
					) {
						return;
					}

					if (
						desiredOpacityBlack ===
						1
					) {
						sendGACustomEvent(
							props.globalContext,
							"r",

							activeCase
								.current
								?.shorthand,
						);
					}

					currentOpacityBlackOverlay.current =
						desiredOpacityBlack;

					const blackOverlayOn =
						currentOpacityBlackOverlay.current ===
						1;

					if (
						blackOverlayOn
					) {
						const allSlideVideos =
							document.querySelectorAll(
								".slide video",
							);

						allSlideVideos.forEach(
							(video) => {
								video.pause();
							},
						);
					} else {
						const activeSlideVideos =
							document.querySelectorAll(
								`[data-slide-id='${activeSlideRef.current}'] video`,
							);

						activeSlideVideos.forEach(
							(video) => {
								const playPromise =
									video.play();

								if (
									playPromise !==
									undefined
								) {
									playPromise.catch(
										() => {},
									);
								}
							},
						);
					}

					gsap.killTweensOf(
						blackOverlayRef.current,
					);

					gsap.killTweensOf(
						btnTimelineCntRef.current,
					);

					gsap.to(
						blackOverlayGradientRef.current,
						0.3,
						{
							autoAlpha:
								blackOverlayOn
									? 1
									: 0,
						},
					);

					gsap.to(
						btnTimelineCntRef.current,
						0.5,
						{
							overwrite:
								"auto",

							autoAlpha:
								blackOverlayOn
									? 0
									: 1,

							yPercent:
								blackOverlayOn
									? 10
									: 0,
						},
					);

					gsap.to(
						blackOverlayGradientTopRef.current,
						0.3,
						{
							autoAlpha:
								blackOverlayOn
									? 1
									: 0,
						},
					);

					gsap.to(
						blackOverlayRef.current,
						0.3,
						{
							autoAlpha:
								currentOpacityBlackOverlay.current,

							overwrite:
								"auto",

							onStart:
								() => {
									if (
										currentOpacityBlackOverlay.current <
										1
									) {
										gsap.set(
											sliderRef.current,
											{
												autoAlpha:
													1,
											},
										);
									}
								},

							onComplete:
								() => {
									gsap.set(
										sliderRef.current,
										{
											autoAlpha:
												currentOpacityBlackOverlay.current ===
												1
													? 0
													: 1,
										},
									);
								},
						},
					);
				};

			/*
			 * ====================================================
			 * HEADER
			 * ====================================================
			 */

			const handleChangeHeaderState =
				(
					dispatcher,
					newState,
					autoClose = true,
				) => {
					const curCase =
						activeCase.current;

					if (
						videoModalOpenRef.current &&
						dispatcher !==
							"handleVideoModalOpen"
					) {
						return;
					}

					switch (
						newState
					) {
						case "default":
							break;

						case "player":
							props.changeHeaderState(
								"logo",
								{
									hideNav:
										true,

									searchBar:
										{
											active:
												false,
										},

									customBack:
										{
											icon:
												isMobileOrTablet
													? null
													: "cross",

											text:
												isMobileOrTablet
													? " "
													: "Close",

											visibilityManaged:
												true,

											variant:
												"video-esc",

											fn:
												handleVideoCloseRequest,
										},
								},
							);

							break;

						case "none":
							props.changeHeaderState(
								"logo",
								{
									searchBar:
										{
											active:
												false,
										},
								},
							);

							break;

						case "slider_overview":
							props.changeHeaderState(
								"logo",
								{
									searchBar:
										{
											active:
												true,
										},
								},
							);

							break;

						case "active_slide":
							if (
								isPortraitModeRef.current
							) {
								props.changeHeaderState(
									"logo",
									{
										searchBar:
											{
												active:
													true,
											},
									},
								);
							} else {
								props.changeHeaderState(
									"logo",
									{
										customBack:
											{
												icon:
													"arrow",

												text:
													`See more ${getFilterLabel(
														caseFilterRef.current,
													)}`,

												fn:
													handleSlideExit,
											},
									},
								);
							}

							break;

						case "active_hover":
							if (
								isPortraitModeRef.current
							) {
								props.changeHeaderState(
									"logo",
									{
										customBack:
											{
												icon:
													"arrow",

												text:
													curCase?.name,

												fn:
													setHoverTimeout,
											},
									},
								);
							} else if (
								isTextScrolledUp()
							) {
								props.changeHeaderState(
									"logo",
									{
										customBack:
											{
												icon:
													"arrow",

												color:
													"active",

												text:
													curCase?.name,

												textPrev:
													getFilterLabel(
														caseFilterRef.current,
													),

												fn:
													handleSlideExit,
											},
									},
								);
							}

							break;

						case "active_title":
							if (
								isPortraitModeRef.current
							) {
								props.changeHeaderState(
									"logo",
									{
										customBack:
											{
												color:
													"active",

												caller:
													"solution_active_title",

												text:
													curCase?.name,
											},
									},
								);
							} else {
								props.changeHeaderState(
									"logo",
									{
										customBack:
											{
												icon:
													"arrow",

												color:
													"active",

												text:
													curCase?.name,

												textPrev:
													getFilterLabel(
														caseFilterRef.current,
													),

												fn:
													handleSlideExit,
											},
									},
								);
							}

							break;

						default:
							break;
					}
				};

			/*
			 * ====================================================
			 * OVERLAY TRANSITION
			 * ====================================================
			 */

			const clearOverlayTransitionTimer =
				() => {
					if (
						overlayTransitionTimer.current
					) {
						clearTimeout(
							overlayTransitionTimer.current,
						);
					}

					if (
						fsTxtEnabledTimer.current
					) {
						clearTimeout(
							fsTxtEnabledTimer.current,
						);
					}
				};

			const setOverlayTransitionTimer =
				(
					_caller,
					_solution,
					cooldown,
					fadeInOnly = false,
				) => {
					fsTxtEnabledTimer.current =
						setTimeout(
							() => {
								handleFsTextScrollEnabledChange(
									true,
								);
							},

							cooldown /
								2,
						);

					overlayTransitionTimer.current =
						setTimeout(
							() => {
								resetActiveVideo();

								handleOverlayInTransChange(
									true,
								);

								clearOverlayTransitionTimer();

								refreshBlackOverlay(
									"setOverlayTransitionTimer",
									-1,
								);

								const playFilmBtns =
									document.querySelectorAll(
										".btn-parallax-sol-pseudo",
									);

								const videoSelectorBtns =
									document.querySelectorAll(
										".sol-fs-btn-parallax-cnt",
									);

								if (
									videoSelectorBtns.length
								) {
									gsap.killTweensOf(
										".sol-fs-btn-parallax-cnt",
									);
								}

								if (
									playFilmBtns.length
								) {
									gsap.killTweensOf(
										".btn-parallax-sol-pseudo",
									);
								}

								if (
									isPortraitModeRef.current
								) {
									gsap.to(
										[
											".sol-fs-progressbar-bullet",
											".sol-fs-btn-hover.arrow",
										],
										0.3,
										{
											delay:
												0.3,

											autoAlpha:
												1,
										},
									);
								}

								const curCase =
									getCurrentCase();

								if (
									curCase
								) {
									btnParallaxSliderRef.current?.setText?.(
										curCase?.[
											"vid-title-0"
										] ??
											"N/A",
										0,
									);

									btnParallaxSliderRef.current?.setText?.(
										curCase?.[
											"vid-title-1"
										] ??
											"N/A",
										1,
									);

									btnParallaxWatchRef.current?.setText?.(
										curCase?.[
											"film-btn-custom-name"
										]?.length >
											0
											? curCase?.[
													"film-btn-custom-name"
											  ]
											: "Watch Film",
									);
								}

								[
									...(playFilmBtns.length
										? playFilmBtns
										: []),

									...(videoSelectorBtns.length
										? videoSelectorBtns
										: []),
								].forEach(
									(item) => {
										if (
											!item
										) {
											return;
										}

										gsap.fromTo(
											item,
											{
												duration:
													0.6,

												ease:
													Expo.easeOut,

												yPercent:
													fadeInOnly
														? 0
														: 100,
											},
											{
												yPercent:
													0,

												autoAlpha:
													1,

												visibility:
													"visible",

												stagger:
													{
														each:
															0.05,

														ease:
															Expo.easeOut,
													},
											},
										);
									},
								);

								const btnTimelineSpacer =
									document.querySelectorAll(
										".sol-fs-btn-timeline-spacer",
									);

								if (
									btnTimelineSpacer.length >
									0
								) {
									gsap.fromTo(
										btnTimelineSpacer,
										{
											duration:
												0.6,

											yPercent:
												250,

											ease:
												Expo.easeOut,
										},
										{
											yPercent:
												0,

											xPercent:
												0,

											autoAlpha:
												1,

											stagger:
												{
													each:
														0.05,

													ease:
														Expo.easeOut,
												},
										},
									);
								}

								fullscreenSolutionRef.current?.initText?.();

								handleOverlayInTransChange(
									false,
								);

								refreshBlackOverlay(
									"setOverlayTransitionTimer",
									-1,
								);

								gsap.to(
									fullscreenText_v2.current,
									{
										duration:
											0.6,

										ease:
											Expo.easeOut,

										autoAlpha:
											1,

										onComplete:
											() => {
												refreshBlackOverlay(
													"setOverlayTransitionTimer",
													-1,
												);
											},
									},
								);
							},

							cooldown,
						);
				};

			/*
			 * ====================================================
			 * HOVER
			 * ====================================================
			 */

			const handleIsCurrentlyHovered =
				(bool) => {
					setIsCurrentlyHovered(
						bool,
					);

					isCurrentlyHoveredRef.current =
						bool;
				};

			const clearHoverTimeout =
				() => {
					if (
						hoverTimeout.current
					) {
						clearTimeout(
							hoverTimeout.current,
						);

						hoverTimeout.current =
							null;
					}
				};

			const handleHover = (
				active,
				blockFadeIn = false,
				hoveredVideo = -1,
				forced = false,
			) => {
				if (
					videoModalOpenRef.current
				) {
					return;
				}

				if (
					(
						!isEnabled ||
						activeSlide ===
							-1
					) &&
					!forced
				) {
					return;
				}

				handleIsCurrentlyHovered(
					active,
				);

				if (active) {
					sendGACustomEvent(
						props.globalContext,
						"h",

						activeCase
							.current
							?.shorthand +
							"-" +
							hoveredVideo,
					);

					handleChangeHeaderState(
						"handleHover",
						"none",
					);

					refreshBlackOverlay(
						"handleHover",
						0,
						true,
					);

					gsap.to(
						fullscreenText_v2.current,
						0.333,
						{
							autoAlpha:
								0,
						},
					);

					if (
						!isPortraitModeRef.current
					) {
						gsap.to(
							".sol-fs-progressbar",
							0.333,
							{
								scaleX:
									0,
							},
						);
					} else {
						gsap.to(
							[
								".sol-fs-progressbar-bullet",
								".sol-fs-btn-hover.arrow",
							],
							0.333,
							{
								autoAlpha:
									0,
							},
						);
					}

					return;
				}

				if (
					!isPortraitModeRef.current
				) {
					gsap.to(
						".sol-fs-progressbar",
						0.333,
						{
							scaleX:
								1,
						},
					);
				} else {
					gsap.to(
						[
							".sol-fs-progressbar-bullet",
							".sol-fs-btn-hover.arrow",
						],
						0.333,
						{
							autoAlpha:
								1,
						},
					);
				}

				if (
					blockFadeIn
				) {
					return;
				}

				refreshBlackOverlay(
					"handleHover",
					-1,
					true,
				);

				gsap.to(
					fullscreenText_v2.current,
					0.333,
					{
						autoAlpha:
							1,
					},
				);
			};

			const setHoverTimeout =
				(instant) => {
					if (
						videoModalOpenRef.current
					) {
						clearHoverTimeout();

						return;
					}

					clearHoverTimeout();

					hoverTimeout.current =
						setTimeout(
							() => {
								scrollDeltaYCurrent.current =
									0;

								props.changeCursorState(
									"idle",
								);

								if (
									!textVisibility.current
								) {
									resetActiveVideo();
								}

								handleHover(
									false,
								);
							},

							instant
								? 0
								: 300,
						);
				};

			/*
			 * ====================================================
			 * SOLUTION OVERLAY
			 * ====================================================
			 */

			const setSolutionOverlay =
				(
					solution,
					cooldown = overlayCoolDown.current,
					instant = false,
					preventFadeOut = false,
				) => {
					if (
						fullscreenText_v2.current
					) {
						fullscreenText_v2.current.scrollTop =
							0;
					}

					clearOverlayTransitionTimer();

					handleOverlayInTransChange(
						true,
					);

					handleFsTextScrollEnabledChange(
						false,
					);

					const playFilmBtns =
						document.querySelectorAll(
							".btn-parallax-sol-pseudo",
						);

					const videoSelectorBtns =
						document.querySelectorAll(
							".sol-fs-btn-parallax-cnt",
						);

					if (
						videoSelectorBtns.length
					) {
						gsap.killTweensOf(
							".sol-fs-btn-parallax-cnt",
						);
					}

					if (
						!preventFadeOut
					) {
						if (
							playFilmBtns.length
						) {
							gsap.killTweensOf(
								".btn-parallax-sol-pseudo",
							);
						}

						[
							...(playFilmBtns.length
								? playFilmBtns
								: []),

							...(videoSelectorBtns.length
								? videoSelectorBtns
								: []),
						].forEach(
							(item) => {
								if (
									item
								) {
									gsap.to(
										item,
										{
											autoAlpha:
												0,

											visibility:
												"hidden",

											overwrite:
												"auto",
										},
									);
								}
							},
						);
					}

					const btnTimelineSpacer =
						document.querySelectorAll(
							".sol-fs-btn-timeline-spacer",
						);

					if (
						btnTimelineSpacer.length >
						0
					) {
						gsap.to(
							btnTimelineSpacer,
							instant
								? 0
								: 0.125,
							{
								autoAlpha:
									0,

								overwrite:
									"auto",
							},
						);
					}

					if (
						!isPortraitModeRef.current
					) {
						gsap.to(
							fullscreenText_v2.current,
							0,
							{
								autoAlpha:
									0,

								overwrite:
									"auto",

								onComplete:
									() => {
										if (
											fullscreenText_v2.current
										) {
											fullscreenText_v2.current.scrollTop =
												0;
										}
									},
							},
						);
					}

					if (
						solution
					) {
						handleSetActiveCase(
							solution,
						);

						handleChangeHeaderState(
							"setSolutionOverlay",
							"active_slide",
						);

						handleSetAutoplay(
							true,
							"setSolutionOverlay",
						);

						setOverlayTransitionTimer(
							"setSolutionOverlay",
							solution,
							cooldown,
							preventFadeOut,
						);

						if (
							!isPortraitModeRef.current
						) {
							gsap.to(
								progressBar.current,
								{
									autoAlpha:
										0,
								},
							);
						}

						return;
					}

					handleSetActiveCase(
						null,
					);

					handleChangeHeaderState(
						"setSolutionOverlay",
						"slider_overview",
					);

					handleHover(
						false,
						true,
						-1,
						true,
					);

					handleTxtVisibility(
						false,
					);

					props.changeCursorState(
						"idle",
					);

					if (
						!isPortraitModeRef.current
					) {
						gsap.to(
							progressBar.current,
							{
								autoAlpha:
									1,
							},
						);
					}
				};

			/*
			 * ====================================================
			 * POINTER EVENTS
			 * ====================================================
			 */

			const setOnDragging =
				(bool) => {
					dragging.current =
						bool;
				};

			const handleTouchStart =
				(e) => {
					let shouldBeHandled =
						false;

					if (
						lastEventTouchStart.current !==
						e.type
					) {
						const time =
							Date.now() -
							lastEventTouchStartTime.current;

						if (
							time >
							250
						) {
							lastEventTouchStartTime.current =
								Date.now();

							lastEventTouchStart.current =
								e.type;

							shouldBeHandled =
								true;
						}
					} else {
						lastEventTouchStart.current =
							e.type;

						lastEventTouchStartTime.current =
							Date.now();

						shouldBeHandled =
							true;
					}

					if (
						!shouldBeHandled
					) {
						e.preventDefault();

						return;
					}

					const classes =
						Array.from(
							e.target
								.classList ??
								[],
						);

					const isFlickity =
						classes.some(
							(c) =>
								c.startsWith(
									"flickity-",
								),
						);

					const shouldPreventHorizontalSwipe =
						!!e.target.closest?.(
							'[class^="preventHorizontalSwipe"]',
						);

					preventHorizontalSwipe.current =
						shouldPreventHorizontalSwipe ||
						isFlickity;

					const shouldPreventSwipe =
						!!e.target.closest?.(
							'[class^="preventSwipe"]',
						);

					if (
						shouldPreventSwipe
					) {
						return;
					}

					nextLiveSlideRef.current =
						-1;

					e.stopImmediatePropagation();

					scrollDeltaYCurrent.current =
						0;

					wasDrag.current =
						false;

					wasSearchClose.current =
						false;

					isLongPress.current =
						false;

					wasTextVisible.current =
						textVisibility.current;

					mobileSwipeDecisionPercentage.current =
						0;

					mobileSwipeDecisionOverlay.current?.fade?.(
						0,
						0,
					);

					mobileSwipeDecisionOverlay.current?.setCurrentAvailable?.(
						[
							activeSlideRef.current >
								0,

							activeSlideRef.current <
								casesRef
									.current
									?.length -
									1,
						],
					);

					setLongPressTimeout();

					dragDirection.current =
						-1;

					setOnDragging(
						true,
					);

					onscroll.current =
						true;

					startProgress.current =
						progress.current;

					startY.current =
						e.clientY ||
						e.touches?.[
							0
						]?.clientY;

					startDrag.current =
						{
							y:
								e.clientY ||
								e.touches?.[
									0
								]?.clientY,

							x:
								e.clientX ||
								e.touches?.[
									0
								]?.clientX,
						};

					let wasSearchBarClick =
						false;

					e.target.classList?.forEach(
						(x) => {
							if (
								x.startsWith(
									"searchbar",
								)
							) {
								wasSearchBarClick =
									true;
							}
						},
					);

					if (
						!wasSearchBarClick
					) {
						if (
							props.isSearchOpen()
						) {
							e.preventDefault();

							wasSearchClose.current =
								true;
						}

						props.closeSearch();
					}

					const pageX =
						e.clientX ||
						e.touches?.[
							0
						]?.clientX ||
						e.pageX;

					if (
						pageX >
							20 &&
						pageX <
							windowSize.current
								.vw -
								20
					) {
						return;
					}

					e.preventDefault();
				};

			const handleTouchMove =
				(e) => {
					e.preventDefault();

					e.stopPropagation();

					const isFlickity =
						Array.from(
							e.target
								.classList ??
								[],
						).some(
							(c) =>
								c.startsWith(
									"flickity-",
								),
						);

					if (
						isFlickity
					) {
						return;
					}

					if (
						!dragging.current
					) {
						return false;
					}

					if (
						dragDirection.current ===
						-1
					) {
						const threshold =
							15;

						const endX =
							e.clientX ||
							e.touches?.[
								0
							]?.clientX ||
							e.pageX;

						const endY =
							e.clientY ||
							e.touches?.[
								0
							]?.clientY ||
							e.pageY;

						const diffX =
							endX -
							startDrag
								.current
								.x;

						const diffY =
							endY -
							startDrag
								.current
								.y;

						const diffXAbs =
							Math.abs(
								diffX,
							);

						const diffYAbs =
							Math.abs(
								diffY,
							);

						if (
							diffYAbs >
							diffXAbs
						) {
							if (
								diffYAbs >
								threshold
							) {
								dragDirection.current =
									1;
							}
						} else if (
							diffXAbs >
							threshold
						) {
							dragDirection.current =
								0;
						}
					}

					if (
						dragDirection.current ===
							-1 ||
						isLongPress.current
					) {
						return;
					}

					clearLongPressTimeout();

					wasDrag.current =
						true;

					if (
						dragDirection.current ===
						0
					) {
						const x =
							e.clientX ||
							e.touches?.[
								0
							]?.clientX ||
							e.pageX;

						const curDelta =
							startDrag
								.current
								.x -
							x;

						let deltaMulti =
							1;

						if (
							curDelta <
							0
						) {
							deltaMulti =
								-1;
						}

						let newProgress =
							startProgress.current;

						if (
							isPortraitModeRef.current
						) {
							newProgress =
								startProgress.current +
								deltaMulti *
									clamp(
										Math.abs(
											curDelta,
										) *
											4,

										0,

										slideWidth.current,
									);

							if (
								preventHorizontalSwipe.current
							) {
								return;
							}

							if (
								wasTextVisible.current
							) {
								mobileSwipeDecisionPercentage.current =
									gsap.utils.clamp(
										-1,
										1,

										(
											startDrag
												.current
												.x -
											x
										) /
											-100,
									);

								mobileSwipeDecisionOverlay.current?.fade?.(
									0.15,
									1,
								);

								mobileSwipeDecisionOverlay.current?.setPosition?.(
									mobileSwipeDecisionPercentage.current,
								);

								return;
							}

							refreshBlackOverlay(
								"handleTouchMove - horizontal",
								3,
							);

							const nextSlide =
								getNextLiveSlide(
									true,
								);

							if (
								nextSlide !==
								activeSlideRef.current
							) {
								setSolutionOverlay(
									null,
								);
							}

							if (
								nextSlide !==
								nextLiveSlideRef.current
							) {
								nextLiveSlideRef.current =
									nextSlide;

								setNextLiveSlide(
									nextSlide,
								);
							}
						} else {
							newProgress =
								startProgress.current +
								curDelta *
									2;
						}

						const progressRight =
							newProgress +
							slidesPerPage.current *
								slideWidth.current;

						if (
							newProgress <
							0
						) {
							newProgress =
								-100;
						} else if (
							progressRight >
							wrapWidth.current
						) {
							newProgress =
								wrapWidth.current -
								(
									casesRef
										.current
										?.length <
									slidesPerPage.current
										? casesRef
												.current
												?.length
										: slidesPerPage.current
								) *
									slideWidth.current +
								100;
						}

						handleProgress(
							"handleTouchMove",
							newProgress,
						);
					} else if (
						dragDirection.current ===
						1
					) {
						if (
							!overflowScrolling.current &&
							!isMobileOrTablet &&
							fsTextScrollEnabledRef.current &&
							!overlayInTransRef.current
						) {
							const y =
								e.clientY ||
								e.touches?.[
									0
								]?.clientY ||
								e.pageY;

							const curDelta =
								startY.current -
								y;

							scrollDeltaYCurrent.current +=
								curDelta *
								2;

							startY.current =
								y;

							refreshBlackOverlay(
								"handleTouchMove - vertical",
							);
						}
					}

					move(
						false,
					);
				};

			const setNextSlide =
				(index) => {
					if (
						activeSlideRef.current !==
						index
					) {
						setNextLiveSlide(
							index,
						);

						handleSetActiveSlide(
							"handleTouchEnd",
							index,
						);

						setSolutionOverlay(
							casesRef
								.current?.[
								index
							],
						);
					} else if (
						dragDirection.current ===
						0
					) {
						setSolutionOverlay(
							casesRef
								.current?.[
								index
							],

							overlayCoolDown.current,
							false,
							true,
						);
					}

					move();
				};

			const handleTouchEnd =
				(e) => {
					e.stopPropagation();

					const delta =
						6;

					const endX =
						e.clientX ||
						e.touches?.[
							0
						]?.clientX ||
						e.pageX;

					const endY =
						e.clientY ||
						e.touches?.[
							0
						]?.clientY ||
						e.pageY;

					const diffX =
						Math.abs(
							endX -
								startDrag
									.current
									.x,
						);

					const diffY =
						Math.abs(
							endY -
								startDrag
									.current
									.y,
						);

					const swipeLeft =
						endX -
							startDrag
								.current
								.x <
						0;

					if (
						diffX <
							delta &&
						diffY <
							delta
					) {
						wasDrag.current =
							false;
					}

					setOnDragging(
						false,
					);

					onscroll.current =
						false;

					const isFlickity =
						Array.from(
							e.target
								.classList ??
								[],
						).some(
							(c) =>
								c.startsWith(
									"flickity-",
								),
						);

					if (
						isFlickity
					) {
						return;
					}

					if (
						isPortraitModeRef.current
					) {
						if (
							preventHorizontalSwipe.current
						) {
							return;
						}

						if (
							wasTextVisible.current
						) {
							mobileSwipeDecisionOverlay.current?.fade?.(
								0.5,
								0,
							);

							if (
								Math.abs(
									mobileSwipeDecisionPercentage.current,
								) <
								1
							) {
								mobileSwipeDecisionOverlay.current?.animatePosition?.(
									0.25,
									0,
								);

								return;
							}

							if (
								activeSlideRef.current -
									mobileSwipeDecisionPercentage.current >
									casesRef
										.current
										?.length -
										1 ||
								activeSlideRef.current -
									mobileSwipeDecisionPercentage.current <
									0
							) {
								mobileSwipeDecisionOverlay.current?.animatePosition?.(
									0.25,
									0,
								);

								return;
							}

							handleProgress(
								"handleTouchEnd+wasTextVisible",

								progress.current +
									mobileSwipeDecisionPercentage.current *
										-1 *
										slideWidth.current,
							);
						} else {
							handleProgress(
								"handleTouchEnd",

								gsap.utils.snap(
									slideWidth.current,

									clamp(
										progress.current +
											(
												diffX >
												slideWidth.current /
													8
													? slideWidth.current
													: 0
											) *
												(
													swipeLeft
														? 1
														: -1
												),

										xProgress.current -
											slideWidth.current /
												2,

										xProgress.current +
											slideWidth.current /
												2,
									),
								),
							);
						}

						const nextActiveSlide =
							getNextLiveSlide();

						setNextSlide(
							nextActiveSlide,
						);

						return;
					}

					move();
				};

			const handleMouseWheel =
				(e) => {
					onscroll.current =
						true;

					e.preventDefault();

					e.stopPropagation();

					let maxDelta =
						0;

					const deltaX =
						e.deltaX;

					if (
						Math.abs(
							e.deltaY,
						) >
						Math.abs(
							deltaX,
						)
					) {
						maxDelta =
							e.deltaY;
					} else {
						maxDelta =
							deltaX;
					}

					let deltaMulti =
						1;

					if (
						maxDelta <
						0
					) {
						deltaMulti =
							-1;
					}

					maxDelta =
						deltaMulti *
						clamp(
							Math.abs(
								maxDelta,
							),
							0,
							200,
						);

					progress.current +=
						isPortraitModeRef.current
							? clamp(
									Math.abs(
										e.deltaX,
									),

									0,

									200,
							  )
							: maxDelta;

					if (
						fsTextScrollEnabledRef.current &&
						!overlayInTransRef.current &&
						fullscreenText_v2.current
					) {
						fullscreenText_v2.current.scrollTop +=
							e.deltaY;
					}

					setWheelTimeout();

					move();
				};

			/*
			 * ====================================================
			 * SLIDE INITIALIZATION
			 * ====================================================
			 */

			const initSlide = (
				slide,
				i,
				duration = 0,
				onComplete = () => {},
			) => {
				const height =
					(
						slideWidth.current *
						9
					) /
					16;

				slide.setAttribute(
					"data-slide-id",
					i,
				);

				slide.setAttribute(
					"data-flip-id",
					i,
				);

				gsap.killTweensOf(
					slide,
				);

				const x =
					slideWidth.current *
					i;

				if (
					!isPortraitModeRef.current
				) {
					gsap.to(
						slide,
						{
							duration,

							x,

							width:
								`${slideWidthPercentage.current * 100}%`,

							height:
								height +
								"px",

							top:
								"50%",

							marginTop:
								`-${height / 2}px`,

							onComplete,

							ease:
								Expo.easeOut,

							scale:
								1,
						},
					);
				} else {
					gsap.to(
						slide,
						{
							duration,

							x,

							width:
								`${slideWidthPercentage.current * 100}%`,

							height:
								"100%",

							overflow:
								"hidden",

							top:
								"0%",

							marginTop:
								"0px",

							onComplete,

							ease:
								Expo.easeOut,

							scale:
								1,
						},
					);
				}

				return x;
			};

			const refreshWindowSizeAndPortrait =
				(
					forced = false,
				) => {
					windowSize.current =
						getWindowSize();

					const newIsPortrait =
						windowSize.current
							.vh >
							windowSize.current
								.vw &&
						isTablet;

					const orientationHasChanged =
						newIsPortrait !==
						isPortraitModeRef.current;

					const isVertical =
						windowSize.current
							.vh >
						windowSize.current
							.vw;

					setCurViewModeIsVertical(
						isVertical,
					);

					if (
						!orientationHasChanged &&
						!forced
					) {
						return;
					}

					if (
						initedSlider.current &&
						isMobileOrTablet &&
						!forced
					) {
						return;
					}

					initedSlider.current =
						true;

					isPortraitModeRef.current =
						isMobileOrTablet &&
						!isTablet
							? true
							: !isMobileOrTablet &&
							  !isTablet
							? false
							: newIsPortrait;

					setIsPortraitMode(
						isPortraitModeRef.current,
					);

					resetActiveVideo();

					handleSlideExit();

					initImages();
				};

			const calculate = (
				_e = null,
				initPosters = false,
				exclude = -1,
			) => {
				refreshWindowSizeAndPortrait();

				const isVertical =
					windowSize.current
						.vw /
						windowSize.current
							.vh <
					1.7777777778;

				const scale =
					isVertical
						? windowSize.current
								.vh /
						  1080
						: windowSize.current
								.vw /
						  1920;

				setCurScale(
					scale,
				);

				setSlidePercentage(
					isPortraitModeRef.current,
				);

				slides.current[0] =
					document.querySelectorAll(
						".slide",
					);

				slideWidth.current =
					windowSize.current
						.vw *
					slideWidthPercentage.current;

				wrapWidth.current =
					slides.current[0]
						.length *
					slideWidth.current;

				if (
					lockScroll.current &&
					exclude === -1
				) {
					return;
				}

				slides.current[0].forEach(
					(
						slide,
						i,
					) => {
						if (
							i ===
							exclude
						) {
							return;
						}

						const xSlideCur =
							initSlide(
								slide,
								i,
							);

						slides.current[1][
							i
						] =
							xSlideCur;
					},
				);

				const wildcardSlide =
					getCaseFromWildcardPath();

				if (
					initPosters
				) {
					setPreloaded(
						true,
					);

					if (
						isPortraitModeRef.current
					) {
						const initialActiveSlide =
							wildcardSlide >
							-1
								? wildcardSlide
								: 0;

						handleSetActiveSlide(
							"calculate",
							initialActiveSlide,
						);

						setSolutionOverlay(
							casesRef
								.current?.[
								initialActiveSlide
							],
						);
					}

					onscroll.current =
						false;

					setIsEnabled(
						true,
					);

					const enterDelay =
						0.5;

					gsap.delayedCall(
						enterDelay,
						() => {
							rafCallback.current =
								() => {
									if (
										!isPortraitModeRef.current &&
										wildcardSlide !==
											-1
									) {
										lockScroll.current =
											true;

										handleSlideEnter(
											wildcardSlide,

											enterDelay ===
												0,
										);
									}
								};
						},
					);
				}

				handleProgress(
					"calculate",

					playrate.current *
						wrapWidth.current,
				);

				move();
			};

			const initSlides =
				(
					indexStartSlide,
				) => {
					calculate(
						null,
						true,
					);

					handleChangeHeaderState(
						"initSlides",
						"slider_overview",
					);

					props.changeView(
						"cases",
					);

					props.initLoad();

					raf.current =
						() => {
							if (
								!lockScroll.current
							) {
								xProgress.current =
									lerp(
										xProgress.current,

										progress.current,

										isPortraitModeRef.current
											? 0.25
											: 0.125,
									);

								playrate.current =
									xProgress.current /
									wrapWidth.current;

								bulletProgressBar.current?.updateProgress?.(
									xProgress.current /
										slideWidth.current,
								);

								if (
									sliderRef.current
								) {
									sliderRef.current.style.transform =
										`translate3d(${-xProgress.current}px,0,0)`;
								}

								const additionalPlayRate =
									xProgress.current /
									(
										wrapWidth.current -
										slidesPerPage.current *
											slideWidth.current
									);

								const offsetMulti =
									clamp(
										1 -
											1 /
												casesRef
													.current
													?.length,

										0,

										0.9,
									);

								if (
									progressBar.current
								) {
									progressBar.current.style.transform =
										`translate3d(${clamp(
											(
												additionalPlayRate -
												1
											) *
												100 *
												offsetMulti,

											-100 *
												offsetMulti,

											0,
										)}%,0,0)`;
								}

								if (
									isPortraitModeRef.current
								) {
									slides.current?.[
										0
									]?.forEach(
										(
											slide,
											i,
										) => {
											const slideX =
												slides
													.current?.[
													1
												][
													i
												];

											const distance =
												(
													progress.current -
													slideX
												) /
												slideWidth.current;

											const scale =
												1 -
												clamp(
													Math.abs(
														distance,
													) /
														4,

													0,

													1,
												);

											gsap.to(
												slide,
												{
													scale,
												},
											);
										},
									);
								}
							} else {
								handleProgress(
									"raf",
									xProgress.current,
								);
							}

							scrollDeltaYTarget.current =
								lerp(
									0,

									scrollDeltaYCurrent.current,

									0.15,
								);

							scrollDeltaYCurrent.current -=
								scrollDeltaYTarget.current;

							if (
								Math.abs(
									scrollDeltaYTarget.current,
								) >
									0.00025 &&
								fullscreenText_v2.current
							) {
								fullscreenText_v2.current.scrollTop +=
									scrollDeltaYTarget.current;
							}
						};

					if (
						indexStartSlide !==
						-1
					) {
						handleMoveToSlide(
							indexStartSlide,
							true,
						);
					}
				};

			const initImages =
				() => {
					const loadImage =
						(
							image,
						) => {
							const portraitMode =
								isPortraitModeRef.current;

							return new Promise(
								(
									resolve,
									reject,
								) => {
									const loadImg =
										new Image();

									loadImg.src =
										`/img/cases/${
											image[
												"shorthand"
											]
										}/poster/${
											image[
												"shorthand"
											]
										}-${
											portraitMode
												? "poster-portrait-900p"
												: "poster-landscape-1600p"
										}.jpg`;

									if (
										portraitMode
									) {
										image[
											"poster-portrait-900p"
										] =
											loadImg.src;
									} else {
										image[
											"poster-landscape-1600p"
										] =
											loadImg.src;
									}

									loadImg.onload =
										() =>
											setTimeout(
												() => {
													resolve(
														image.url,
													);
												},
												0,
											);

									loadImg.onerror =
										(err) =>
											reject(
												err,
											);
								},
							);
						};

					const startInit =
						() => {
							const startSlideIndex =
								getCaseFromWildcardPath();

							initSlides(
								startSlideIndex,
							);
						};

					Promise.all(
						caseData.map(
							(
								solution,
							) =>
								loadImage(
									solution,
								),
						),
					)
						.then(
							() => {
								startInit();
							},
						)
						.catch(
							(err) => {
								startInit();

								console.log(
									"Failed to load images",
									err,
								);
							},
						);
				};

			/*
			 * ====================================================
			 * SLIDE ENTER / EXIT
			 * ====================================================
			 */

			const handleSlideClick =
				(id) => {
					if (
						wasSearchClose.current ||
						wasDrag.current ||
						isPortraitModeRef.current ||
						isLongPress.current
					) {
						return false;
					}

					lockScroll.current =
						true;

					const isActive =
						activeSlideRef.current ===
						id;

					if (
						!isActive
					) {
						handleSlideEnter(
							id,
						);
					} else {
						handleSlideExit();
					}
				};

			const handleSlideEnter =
				(
					id,
					instant = false,
				) => {
					const activeSlideEl =
						document.querySelectorAll(
							`[data-slide-id='${id}']`,
						)?.[0] ??
						null;

					if (
						!activeSlideEl
					) {
						return;
					}

					const allOtherSlides =
						document.querySelectorAll(
							`.slide:not([data-slide-id='${id}'])`,
						);

					const center =
						xProgress.current;

					const curWidth =
						slideWidth.current;

					const curHeight =
						curWidth *
						0.5625;

					const isVertical =
						windowSize.current
							.vw /
							windowSize.current
								.vh <
						1.7777777778;

					const scale =
						isVertical
							? windowSize.current
									.vh /
							  curHeight
							: windowSize.current
									.vw /
							  curWidth;

					handleSetActiveSlide(
						"handleSlideEnter",
						id,
					);

					setSolutionOverlay(
						casesRef
							.current?.[
							id
						],

						500,
					);

					allOtherSlides.forEach(
						(slide) => {
							slide.style.zIndex =
								0;
						},
					);

					activeSlideEl.style.zIndex =
						10;

					gsap.killTweensOf(
						activeSlideEl,
					);

					gsap.to(
						activeSlideEl,

						instant
							? 0
							: 0.75,

						{
							x:
								center +
								windowSize.current
									.vw /
									2 -
								curWidth /
									2,

							autoAlpha:
								1,

							ease:
								Expo.easeInOut,

							scale,

							onComplete:
								() => {
									setTimeout(
										() => {
											gsap.set(
												activeSlideEl,
												{
													top:
														0,

													marginTop:
														0,

													x:
														center,

													backgroundColor:
														"rgba(0,0,0,1)",

													scale:
														1,

													width:
														"100%",

													height:
														"100%",
												},
											);
										},

										50,
									);
								},
						},
					);
				};

			const handleSlideExit =
				() => {
					const activeSlideId =
						activeSlideRef.current;

					const activeSlideEl =
						document.querySelectorAll(
							`[data-slide-id='${activeSlideId}']`,
						)?.[0] ??
						null;

					if (
						!activeSlideEl
					) {
						return;
					}

					handleSetActiveSlide(
						"handleSlideExit",
						-1,
					);

					handleSetActiveCase(
						null,
					);

					setActiveVideo(
						-1,
					);

					handleHover(
						false,
						true,
					);

					clearHoverTimeout();

					setSolutionOverlay(
						null,
					);

					refreshBlackOverlay(
						"handleSlideExit",
						0,
					);

					calculate(
						null,
						false,
						activeSlideId,
					);

					lockScroll.current =
						false;

					initSlide(
						activeSlideEl,
						activeSlideId,
						0.666,
						() => {
							activeSlideEl.style.zIndex =
								1;
						},
					);
				};

			/*
			 * ====================================================
			 * NAVIGATION BUTTONS
			 * ====================================================
			 */

			const handleBulletClick =
				(i) => {
					handleMoveToSlide(
						i,
						false,
					);

					setNextSlide(
						i,
					);
				};

			const handleArrowClick =
				(direction) => {
					const nextSlide =
						gsap.utils.clamp(
							0,

							casesRef
								.current
								.length -
								1,

							activeSlideRef.current +
								direction,
						);

					handleMoveToSlide(
						nextSlide,
						false,
					);

					setNextSlide(
						nextSlide,
					);
				};

			const handleVisitSite =
				() => {
					const url =
						casesRef
							.current?.[
							activeSlide
						]?.url;

					if (
						!url
					) {
						return;
					}

					window
						.open(
							url,
							"_blank",
						)
						?.focus?.();
				};

			/*
			 * ====================================================
			 * GLOBAL EVENT LIFECYCLE
			 * ====================================================
			 */

			const handleScrollRefresh =
				() => {
					if (
						activeSlideRef.current ===
						-1
					) {
						return;
					}

					refreshBlackOverlay(
						"scroll throttle",
					);
				};

			useEffect(
				() => {
					const fullscreenText =
						fullscreenText_v2.current;

					fullscreenText?.addEventListener(
						"scroll",
						handleScrollRefresh,
					);

					requestRef.current =
						requestAnimationFrame(
							animate,
						);

					window.addEventListener(
						"touchstart",
						handleTouchStart,
						{
							passive:
								false,
						},
					);

					window.addEventListener(
						"wheel",
						handleMouseWheel,
						{
							passive:
								false,
						},
					);

					window.addEventListener(
						"resize",
						calculate,
					);

					window.addEventListener(
						"touchmove",
						handleTouchMove,
					);

					window.addEventListener(
						"touchend",
						handleTouchEnd,
					);

					window.addEventListener(
						"mousedown",
						handleTouchStart,
					);

					window.addEventListener(
						"mousemove",
						handleTouchMove,
					);

					window.addEventListener(
						"mouseup",
						handleTouchEnd,
					);

					return () => {
						fullscreenText?.removeEventListener(
							"scroll",
							handleScrollRefresh,
						);

						cancelAnimationFrame(
							requestRef.current,
						);

						window.removeEventListener(
							"touchstart",
							handleTouchStart,
						);

						window.removeEventListener(
							"wheel",
							handleMouseWheel,
						);

						window.removeEventListener(
							"resize",
							calculate,
						);

						window.removeEventListener(
							"touchmove",
							handleTouchMove,
						);

						window.removeEventListener(
							"touchend",
							handleTouchEnd,
						);

						window.removeEventListener(
							"mousedown",
							handleTouchStart,
						);

						window.removeEventListener(
							"mousemove",
							handleTouchMove,
						);

						window.removeEventListener(
							"mouseup",
							handleTouchEnd,
						);

						clearOverlayTransitionTimer();

						clearHoverTimeout();

						clearLongPressTimeout();

						clearWheelTimeout();
					};
				},
				[],
			);

			/*
			 * ====================================================
			 * FILTER CHANGE
			 * ====================================================
			 */

			useEffect(
				() => {
					caseFilterRef.current =
						caseFilter;

					if (
						!initialCaseFilterTriggered.current
					) {
						initialCaseFilterTriggered.current =
							true;

						return;
					}

					setSolutionOverlay(
						null,
						0,
						true,
					);

					gsap.to(
						".slide",
						{
							duration:
								0.3,

							onComplete:
								() => {
									progress.current =
										0;

									xProgress.current =
										0;

									handleSetActiveSlide(
										"useEffect [caseFilter]",
										-1,
									);

									handleSetActiveCase(
										null,
									);

									setActiveVideo(
										-1,
									);

									handleHover(
										false,
										true,
									);

									setIsEnabled(
										false,
									);

									setPreloaded(
										false,
									);

									clearHoverTimeout();

									refreshCases();

									navigate(
										getFilterOverviewPath(
											caseFilter,
										) +
											(
												location.search ??
												""
											),
										{
											replace:
												false,
										},
									);
								},
						},
					);
				},
				[
					caseFilter,
				],
			);

			useLayoutEffect(
				() => {
					refreshWindowSizeAndPortrait(
						true,
					);
				},
				[
					cases,
				],
			);

			const debugComplication =
				-1;

			const currentFullscreenCase =
				cases?.[0]?.[
					nextLiveSlide
				];

			return (
				<div>
					<SolutionsSlider
						cases={
							cases
						}
						preloaded={
							preloaded
						}
						isPortraitMode={
							isPortraitMode
						}
						isOfflineMode={
							isOfflineMode
						}
						debugComplication={
							debugComplication
						}
						isEnabled={
							isEnabled
						}
						activeVideo={
							activeVideo
						}
						curScale={
							curScale
						}
						windowWidth={
							windowSize.current
								.vw
						}
						activeSlide={
							activeSlide
						}
						cKey={
							props.cKey
						}
						onscroll={
							onscroll
						}
						sliderWrapperRef={
							sliderWrapperRef
						}
						sliderRef={
							sliderRef
						}
						titleContainerRef={
							titleContainer
						}
						centerTitleIndexRef={
							centerTitleIndex
						}
						centerTitleNameRef={
							centerTitleName
						}
						blackOverlayRef={
							blackOverlayRef
						}
						handleSlideClick={
							handleSlideClick
						}
						nextLiveSlide={
							nextLiveSlide
						}
					/>

					<div
						className={`black-overlay-g ${styles.hiddenGradient}`}
						ref={
							blackOverlayGradientRef
						}
					/>

					<div
						className={`black-overlay-g top ${styles.hiddenGradient}`}
						ref={
							blackOverlayGradientTopRef
						}
					/>

					<div className="black-overlay-g bottom-bg" />

					<SolutionsFullscreenText
						activeSlide={
							activeSlide
						}
						nextLiveSlide={
							nextLiveSlide
						}
						fsTextScrollEnabled={
							fsTextScrollEnabled
						}
						overlayInTrans={
							overlayInTrans
						}
						debugComplication={
							debugComplication
						}
						isPortraitMode={
							isPortraitMode
						}
						isPortraitModeRef={
							isPortraitModeRef
						}
						isCurrentlyHoveredRef={
							isCurrentlyHoveredRef
						}
						wasSearchCloseRef={
							wasSearchClose
						}
						wasDragRef={
							wasDrag
						}
						isLongPressRef={
							isLongPress
						}
						fullscreenTextRef={
							fullscreenText_v2
						}
						fullscreenSolutionRef={
							fullscreenSolutionRef
						}
						windowSize={
							windowSize.current
						}
						solution={
							currentFullscreenCase
						}
						caseFilter={
							caseFilter
						}
						setHoverTimeout={
							setHoverTimeout
						}
						handleSlideExit={
							handleSlideExit
						}
						setNewFilter={
							setNewFilter
						}
						handleVideoModalOpen={
							handleVideoModalOpen
						}
						handleVisitSite={
							handleVisitSite
						}
					/>

					<SolutionsControls
						cases={
							cases
						}
						activeSlide={
							activeSlide
						}
						isPortraitMode={
							isPortraitMode
						}
						isCurrentlyHovered={
							isCurrentlyHovered
						}
						overlayInTrans={
							overlayInTrans
						}
						btnTimelineCntRef={
							btnTimelineCntRef
						}
						progressBarRef={
							progressBar
						}
						bulletProgressBarRef={
							bulletProgressBar
						}
						btnParallaxWatchRef={
							btnParallaxWatchRef
						}
						btnParallaxSliderRef={
							btnParallaxSliderRef
						}
						fullscreenSolutionRef={
							fullscreenSolutionRef
						}
						handleArrowClick={
							handleArrowClick
						}
						handleBulletClick={
							handleBulletClick
						}
						handleVideoModalOpen={
							handleVideoModalOpen
						}
						clearHoverTimeout={
							clearHoverTimeout
						}
						setActiveVideo={
							setActiveVideo
						}
						handleHover={
							handleHover
						}
						setHoverTimeout={
							setHoverTimeout
						}
					/>

					<SolutionsUtilityLayer
						cases={
							cases
						}
						activeSlide={
							activeSlide
						}
						activeVideo={
							activeVideo
						}
						autoPlay={
							autoPlay
						}
						isCurrentlyHovered={
							isCurrentlyHovered
						}
						scrollDirectionRef={
							scrollDirection
						}
						curViewModeIsVertical={
							curViewModeIsVertical
						}
						initialViewVertical={
							initialViewVertical.current
						}
						mobileSwipeDecisionOverlayRef={
							mobileSwipeDecisionOverlay
						}
						videoModalRef={
							videoModalRef
						}
						isOfflineMode={
							isOfflineMode
						}
						changeCursorState={
							props.changeCursorState
						}
						handleVideoCloseRequest={
							handleVideoCloseRequest
						}
						handleVideoChromeVisibilityChange={
							handleVideoChromeVisibilityChange
						}
					/>
				</div>
			);
		},
	);

export default React.memo(
	Solutions,
);